import { cn } from '@/lib/utils';
import React from 'react'
import { useTranslation } from 'react-i18next';
import { TruncatedText } from '@/components/common/truncated-text';
import { CopyClipboard } from '@/components/common/copy-clipboard';
import { formatToFullDateTime } from '@/utils/date-formatter';
import { IconButton } from '@/components/common/icon-button';
import { Pagination } from '@/components/common/pagination';
import { LoadingOverlay } from '@/components/common/loading-overlay';
import { agentOrderHooks } from '@/manager';
import { useI18nNavigate } from '@/hooks/use-i18n-navigate';
import { Eye } from 'lucide-react';
import { OrderDetailModal } from './order-detail-modal';
import { OrderUserData } from '@/services/agent-v2-order-user-list';
import { getOrderUserStatusBadgeClass } from './order-user-status';

interface TableListProps {
  orderUsers: OrderUserData[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  changePage: (page: number) => void;
  search: () => void;
}

/** 智能体订单管理-订单表格列表 */
export const OrderTableList = ({
  orderUsers,
  loading,
  total,
  page,
  pageSize,
  changePage,
  search,
}: TableListProps) => {
  const { t } = useTranslation();
  const navigate = useI18nNavigate();

  const { orderDetail, selectedOrderDetail } = agentOrderHooks.useOrderDetail()

  const handleDetail = (order: OrderUserData) => {
    selectedOrderDetail(order);
  }

  const handleSizeChange = (page: number) => {
    changePage(page);
    search();
  }

  const orderTypeMap = {
    '1': t('agent_dashboard.order_management.table.order_type_map.year_vip'),
    '2': t('agent_dashboard.order_management.table.order_type_map.half_year_vip'),
    '3': t('agent_dashboard.order_management.table.order_type_map.quarter_vip'),
  }

  const orderStatusLabel = (status: number | undefined) => {
    if (status == null || Number.isNaN(Number(status))) return '—';
    return t(`agent_dashboard.order_management.table.order_status_map.${status}`, { defaultValue: '—' });
  };

  return (
    <div className='relative flex-1 flex flex-col rounded-lg border overflow-hidden'>
      <div
        className="overflow-auto flex-1"
      >
        <table className="w-full rounded-t-lg relative">
          <thead className='bg-gray-50 sticky top-0 border-b border-[rgba(234,236,240,1)] h-11 z-20'>
            <tr className="bg-gray-50">
              <Th>{t('agent_dashboard.order_management.table.order_no')}</Th>
              <Th>
                {t('agent_dashboard.order_management.table.username')}/
                {t('agent_dashboard.order_management.table.id')}</Th>
              <Th>
                {t('agent_dashboard.order_management.table.operator')}/
                {t('agent_dashboard.order_management.table.id')}
              </Th>
              <Th>{t('agent_dashboard.order_management.table.order_type')}</Th>
              <Th>{t('agent_dashboard.order_management.table.status')}</Th>
              <Th className='min-w-[7rem]'>{t('agent_dashboard.permission.table.create_time')}</Th>
              <Th>{t('agent_dashboard.order_management.table.remark')}</Th>
              <Th className='sticky right-0 bg-gray-50 min-w-[7rem] max-w-28 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]'></Th>
            </tr>
          </thead>
          <tbody className='bg-white'>
            {orderUsers?.length > 0 && orderUsers?.map((item, index) => (
              <tr key={`${item.id}_${index}`} className='border-b h-18 transition-colors duration-200'>
                <Td ><TruncatedText text={item.orderNo} className='text-black' /></Td>
                <Td className='min-w-[8rem]'>
                  <div>
                    <TruncatedText text={item.userName} className='text-black pb-1' />
                    <div className='flex items-center'>
                      <TruncatedText text={item.userId} className='text-sm mr-1' />
                      <CopyClipboard text={item.userId} />
                    </div>
                  </div>
                </Td>
                <Td className='min-w-[8rem]'>
                  <TruncatedText text={item.operatorName} className='text-black' />
                  <div className='flex items-center'>
                    <TruncatedText text={item.operatorId} className='text-sm mr-1' />
                    <CopyClipboard text={item.operatorId} />
                  </div>
                </Td>
                <Td className='text-[rgba(16,24,40,1)]'>
                  {item.productName}
                </Td>
                <Td className='w-50'>
                  <span className={cn(
                    "py-1 px-2 rounded-lg text-sm font-medium",
                    getOrderUserStatusBadgeClass(item.status),
                  )}>{orderStatusLabel(item.status)}</span>
                </Td>
                <Td className=''>{formatToFullDateTime(item.createdAt)}</Td>
                <Td className='min-w-[11rem]'>
                  <TruncatedText text={item.description} className='text-black' />
                </Td>
                <Td className='sticky right-0 bg-white min-w-[7rem] max-w-28 z-[5]  group-hover:bg-gray-50 space-x-5 flex items-center py-4 h-full'>
                  <OrderDetailModal
                    orderDetail={orderDetail}
                    trigger={
                      <IconButton
                        className='size-10 rounded-[8px]'
                        icon={<Eye className='size-6' />}
                        onClick={() => handleDetail(item)}
                      />
                    }
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className='h-[4.25rem] border-t border-[rgba(234,236,240,1)] px-6 flex items-center'>
        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onChange={handleSizeChange}
        />
      </div>
      <LoadingOverlay loading={loading} />
    </div>
  )
}

const Th = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <th className={cn(
    'min-w-[6.25rem] max-w-[12.5rem] pl-6  text-left text-sm font-semibold text-[rgba(16,24,40,1)]',
    className,
  )}>
    {children}
  </th>
)

const Td = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <td className={cn(
    'min-w-[6.25rem] max-w-[12.5rem] pl-6 text-sm text-[rgba(71,84,103,1)]',
    className,
  )}>
    {children}
  </td>
)